import React, { useState } from "react";
import { FiUser, FiCheck, FiClock, FiX, FiInfo, FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import AddSenderModal from "./AddSenderModal";

const SenderIdSelector = ({
  selectedSenderId,
  setSelectedSenderId,
  onAddSender,
  senderIds,
  isLoading = false,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAllSenders, setShowAllSenders] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const sendersPerPage = 4;

  // Filter senders based on showAllSenders toggle
  const filteredByStatus = showAllSenders
    ? senderIds
    : senderIds.filter((sender) => sender.status.toLowerCase() === "approved");

  // Filter senders based on search query
  const filteredBySearch = searchQuery.trim()
    ? filteredByStatus.filter((sender) =>
        (sender.sender || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
      )
    : filteredByStatus;

  // Pagination logic
  const totalPages = Math.ceil(filteredBySearch.length / sendersPerPage);
  const startIndex = (currentPage - 1) * sendersPerPage;
  const endIndex = startIndex + sendersPerPage;
  const displaySenderIds = filteredBySearch.slice(startIndex, endIndex);

  // Reset to page 1 when search query or filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showAllSenders]);

  const pendingCount = senderIds.filter(
    (sender) => sender.status.toLowerCase() === "pending"
  ).length;
  const approvedCount = senderIds.filter(
    (sender) => sender.status.toLowerCase() === "approved"
  ).length;

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <FiCheck className="w-4 h-4 text-red-500" />;
      case "pending":
        return <FiClock className="w-4 h-4 text-yellow-500" />;
      case "rejected":
        return <FiX className="w-4 h-4 text-red-500" />;
      default:
        return <FiInfo className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "border-gray-200 bg-white";
      case "pending":
        return "border-yellow-200 bg-white";
      case "rejected":
        return "border-red-200 bg-white";
      default:
        return "border-gray-200 bg-white";
    }
  };

  const getStatusText = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "Ready to use";
      case "pending":
        return "Under review";
      case "rejected":
        return "Not approved";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="p-6 mb-6 bg-white rounded-2xl border border-red-100 shadow-xl">
      <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-50 rounded-xl border border-red-200">
            <FiUser className="text-xl text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800 sm:text-xl">
              Sender ID
            </h3>
            <p className="text-xs text-gray-600 sm:text-sm">
             select or create a custom sender identity for your messages
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Show Pending Toggle */}
          {pendingCount > 0 && (
            <button
              onClick={() => setShowAllSenders(!showAllSenders)}
              className="flex justify-center items-center px-3 py-2 space-x-2 text-sm text-yellow-700 bg-yellow-100 rounded-lg transition-colors hover:bg-yellow-200"
            >
              <FiClock className="text-sm" />
              <span>
                {showAllSenders
                  ? "Hide Pending"
                  : `Show Pending (${pendingCount})`}
              </span>
            </button>
          )}

          <button
            onClick={() => setShowAddModal(true)}
            disabled={isLoading}
            className="flex justify-center items-center px-4 py-2 space-x-2 w-full text-white bg-red-600 rounded-lg shadow-md transition-all transform hover:bg-red-700 hover:scale-105 disabled:bg-gray-300 disabled:text-gray-500 disabled:transform-none sm:w-auto"
          >
            <FiCheck className="text-lg" />
            <span className="text-sm sm:text-base">
              {isLoading ? "Loading..." : "Add Sender ID"}
            </span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {!isLoading && senderIds.length > 0 && (
        <div className="mb-4 ">
          <div className="relative  ">
            <FiSearch className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search sender IDs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="py-3 border border-gray-600 rounded-lg pl-10 w-full text-gray-700 bg-gray-100 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 text-gray-400 transition-colors -translate-y-1/2 hover:text-gray-600"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 text-sm text-gray-600">
              Found {filteredBySearch.length} sender ID
              {filteredBySearch.length !== 1 ? "s" : ""} matching "{searchQuery}"
            </div>
          )}
        </div>
      )}

      {/* Sender ID Summary */}
      {!isLoading && senderIds.length > 0 && (
        <div className="p-3 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">Approved: {approvedCount}</span>
              </div>
              {pendingCount > 0 && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-600">Pending: {pendingCount}</span>
                </div>
              )}
            </div>
            <div className="text-gray-500">
              {showAllSenders
                ? "Showing all sender IDs"
                : "Showing approved only"}
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full border-2 border-red-500 animate-spin border-t-transparent"></div>
            <span className="text-gray-600">Loading sender IDs...</span>
          </div>
        </div>
      )}

      {/* Sender ID Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {displaySenderIds.length === 0 ? (
            <div className="col-span-full py-8 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                <FiUser className="text-2xl text-gray-400" />
              </div>
              <h4 className="mb-2 text-lg font-semibold text-gray-700">
                No Sender IDs Available
              </h4>
              <p className="mb-4 text-gray-500">
                You need to create a sender ID before you can send messages
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg shadow-md transition-all hover:bg-red-700"
              >
                Create Your First Sender ID
              </button>
            </div>
          ) : (
            displaySenderIds.map((sender) => (
              <div
                key={sender.id}
                onClick={() =>
                  sender.status.toLowerCase() === "approved" &&
                  setSelectedSenderId(sender.sender)
                }
                className={`relative p-2 rounded-lg border cursor-pointer transition-all ${
                  selectedSenderId === sender.sender
                    ? "border-red-400 bg-red-50 shadow-md"
                    : sender.status === "approved"
                    ? "border-gray-200 bg-white hover:border-red-300 hover:shadow-sm"
                    : getStatusColor(sender.status)
                } ${
                  sender.status !== "approved"
                    ? "cursor-not-allowed opacity-75"
                    : ""
                }`}
              >
                {/* Selection indicator */}
                {selectedSenderId === sender.sender && (
                  <div className="absolute -top-1 -right-1 p-0.5 text-white bg-red-600 rounded-full">
                    <FiCheck className="w-3 h-3" />
                  </div>
                )}

                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                    <div className="flex-shrink-0 p-1 bg-white rounded border border-gray-100">
                      <FiUser className="w-3.5 h-3.5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-semibold text-gray-800 truncate">
                        {sender.sender}
                      </h4>
                      <span
                        className={`text-[10px] px-1 py-0.5 rounded-full ${
                          sender.type === "default"
                            ? "bg-red-50 text-red-600 border border-red-100"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {sender.type === "default" ? "Default" : "Custom"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-shrink-0 items-center">
                    <div className="w-3.5 h-3.5">
                      {getStatusIcon(sender.status)}
                    </div>
                  </div>
                </div>

                <div className="text-[10px] text-gray-600">
                  <span className="font-medium">Status:</span>{" "}
                  {getStatusText(sender.status)}
                </div>

                {sender.status === "pending" && (
                  <div className="p-1 mt-1 text-[10px] text-yellow-600 bg-yellow-50 rounded border border-yellow-200">
                    ⏱️ Processing...
                  </div>
                )}

                {sender.status === "rejected" && (
                  <div className="p-1 mt-1 text-[10px] text-red-600 bg-red-50 rounded border border-red-200">
                    ❌ Rejected
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!isLoading && filteredBySearch.length > sendersPerPage && (
        <div className="flex flex-col gap-3 mt-6 sm:flex-row sm:justify-between sm:items-center sm:gap-4">
          <div className="text-xs text-gray-600 sm:text-sm text-center sm:text-left">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredBySearch.length)} of{" "}
            {filteredBySearch.length} sender ID{filteredBySearch.length !== 1 ? "s" : ""}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
            {/* Mobile pagination - simplified */}
            <div className="flex items-center justify-center gap-1 sm:hidden">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                <FiChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Prev</span>
              </button>

              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-600 px-2">
                  {currentPage} of {totalPages}
                </span>
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Desktop pagination - full controls */}
            <div className="hidden sm:flex gap-2 items-center">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                <FiChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <div className="flex gap-1 items-center">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                        currentPage === pageNum
                          ? "bg-red-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:border-red-300"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}~
              </div>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                Next
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      {/* <div className="p-4 mt-6 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-start space-x-3">
          <FiInfo className="text-blue-500 text-lg mt-0.5" />
          <div>
            <h4 className="mb-2 font-semibold text-blue-800">Sender ID Guidelines</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Maximum 11 characters (letters, numbers, and spaces)</li>
              <li>• No special characters or symbols allowed</li>
              <li>• Must contain at least one letter (cannot be only numbers)</li>
              <li>• Custom sender IDs require approval (24-48 hours)</li>
              <li>• Use your business name or recognizable brand</li>
            </ul>
          </div>
        </div>
      </div> */}

      {/* Add Sender Modal */}
      {showAddModal && (
        <AddSenderModal
          onClose={() => setShowAddModal(false)}
          onAdd={onAddSender}
        />
      )}
    </div>
  );
};

export default SenderIdSelector;
